/**
 * Wire schemas for module 7 (QS commercial): the main contract, interim
 * valuations and their measurement sheets, payment certificates and payments,
 * retention, provisional / prime-cost sums, adjustments, the variation
 * register, notices, claims, disputes, the final account, reconciliation,
 * cost control, cash flow and cost plans.
 *
 * Synced to the device: the surveyor's valuations (read-only) and its
 * measurement rows (recorded offline — independent rows, edits merged per
 * field). Everything else is online. Money is `{amount_minor, currency}`;
 * quantities and percentages arrive as decimal strings and are never turned
 * into floats for money. Optional fields default so a newer server never
 * breaks an older client.
 */
import { z } from 'zod';

const text = z.string().default('');
const date = z.string().nullable().default(null);
const nullableId = z.string().nullable().default(null);
const version = z.number().int().nullable().default(null);
const count = z.coerce.number().catch(0).default(0);
const bool = z.boolean().catch(false).default(false);
const decimal = z.string().nullable().default(null);

export const moneySchema = z.object({ amount_minor: z.number().int(), currency: z.string() });
export type Money = z.infer<typeof moneySchema>;
const money = moneySchema.nullable().catch(null).default(null);

const personSchema = z
  .object({ id: z.string(), name: z.string().default('') })
  .nullable()
  .catch(null)
  .default(null);
export type Person = NonNullable<z.infer<typeof personSchema>>;

export const commercialPartySchema = z
  .object({
    id: z.string(),
    party_role: z.string().default(''),
    organization_name: z.string().default(''),
  })
  .nullable()
  .catch(null)
  .default(null);
export type CommercialParty = NonNullable<z.infer<typeof commercialPartySchema>>;

/** The actions the server says this person may take now (absent keys mean no). */
function can<const K extends string>(...keys: K[]): z.ZodType<Record<K, boolean>> {
  const pick = (raw: unknown) =>
    Object.fromEntries(
      keys.map((k) => [
        k,
        !!raw && typeof raw === 'object' && (raw as Record<string, unknown>)[k] === true,
      ]),
    );
  return z.unknown().transform(pick) as unknown as z.ZodType<Record<K, boolean>>;
}

// ---------------------------------------------------------------------------
// Vocabularies
// ---------------------------------------------------------------------------

export const CONTRACT_FORMS = ['jbc', 'fidic_red', 'fidic_yellow', 'ppra', 'other'] as const;
export type ContractForm = (typeof CONTRACT_FORMS)[number];
export const CERTIFIERS = ['consultant', 'owners_pm'] as const;
export type Certifier = (typeof CERTIFIERS)[number];
export const LINE_KINDS = [
  'boq_item',
  'variation',
  'claim',
  'dayworks',
  'materials',
  'adjustment',
] as const;
export type LineKind = (typeof LINE_KINDS)[number];
export const LINE_METHODS = ['quantity', 'percent', 'value'] as const;
export type LineMethod = (typeof LINE_METHODS)[number];
export const CERTIFICATE_KINDS = ['interim', 'advance', 'retention_release', 'final'] as const;
export type CertificateKind = (typeof CERTIFICATE_KINDS)[number];
export const CERTIFICATE_STATUSES = [
  'draft',
  'contractor_review',
  'disputed',
  'awaiting_certification',
  'certified',
  'payment_approved',
  'part_paid',
  'paid',
  'cancelled',
] as const;
export type CertificateStatus = (typeof CERTIFICATE_STATUSES)[number];
export const PAYMENT_METHODS = [
  'bank_transfer',
  'rtgs',
  'eft',
  'cheque',
  'mobile_money',
  'cash',
  'other',
] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];
export const ADJUSTMENT_CATEGORIES = [
  'contra_charge',
  'liquidated_damages',
  'remeasurement',
  'fluctuation',
  'other',
] as const;
export type AdjustmentCategory = (typeof ADJUSTMENT_CATEGORIES)[number];
export const NOTICE_KINDS = [
  'delay',
  'eot',
  'loss_expense',
  'variation',
  'instruction',
  'default',
  'other',
] as const;
export type NoticeKind = (typeof NOTICE_KINDS)[number];
export const CLAIM_KINDS = ['eot', 'money', 'both'] as const;
export type ClaimKind = (typeof CLAIM_KINDS)[number];
export const DISPUTE_STATUSES = [
  'open',
  'negotiation',
  'adjudication',
  'arbitration',
  'resolved',
  'withdrawn',
] as const;
export type DisputeStatus = (typeof DISPUTE_STATUSES)[number];
export const VARIATION_BASES = [
  'boq_rates',
  'star_rates',
  'dayworks',
  'quotation',
  'lump_sum',
] as const;
export type VariationBasis = (typeof VARIATION_BASES)[number];

/** Editable fields of a measurement row (offline three-way merge). */
export const MEASUREMENT_EDITABLE = [
  'description',
  'times',
  'length',
  'width',
  'height',
  'deduct',
  'drawing_revision_id',
  'zone_id',
  'location',
  'measured_on',
] as const;

// ---------------------------------------------------------------------------
// The contract
// ---------------------------------------------------------------------------

export const contractSchema = z.object({
  id: z.string(),
  project_id: z.string().default(''),
  kind: z.string().default('main'),
  number: count,
  reference: text,
  title: text,
  employer_party: commercialPartySchema,
  contractor_party: commercialPartySchema,
  form: z.string().default('jbc'),
  form_note: text,
  status: z.string().default('draft'),
  boq_id: nullableId,
  boq_name: z.string().nullable().default(null),
  boq_version_number: z.number().nullable().catch(null).default(null),
  currency: z.string().default('KES'),
  contract_sum: money,
  sum_source: z.string().default('entered'),
  retention_percent: decimal,
  retention_limit_percent: decimal,
  retention_limit: money,
  retention_pc_release_percent: count,
  advance: money,
  advance_recovery_percent: decimal,
  payment_period_days: count,
  vat_percent: decimal,
  wht_percent: decimal,
  fluctuation: bool,
  certifier: z.string().default('consultant'),
  payee_account: text,
  start_date: date,
  completion_date: date,
  notes: text,
  activated_at: date,
  terms_locked: bool,
  version,
  can: can('edit', 'activate'),
});
export type Contract = z.infer<typeof contractSchema>;

export const contractViewSchema = z.object({
  contract: contractSchema.nullable().catch(null).default(null),
  can_create: bool,
  boqs: z
    .array(z.object({ id: z.string(), name: text, approved_total: money }))
    .catch([])
    .default([]),
});
export type ContractView = z.infer<typeof contractViewSchema>;

// ---------------------------------------------------------------------------
// Valuations and measurement
// ---------------------------------------------------------------------------

export const valuationLineSchema = z.object({
  id: z.string(),
  kind: z.enum(LINE_KINDS).catch('boq_item'),
  sequence: count,
  boq_lineage: nullableId,
  item_kind: z.string().nullable().default(null),
  section: text,
  item_code: text,
  description: text,
  unit: text,
  contract_quantity: decimal,
  rate: money,
  contract_amount: money,
  variation_id: nullableId,
  claim_id: nullableId,
  adjustment_id: nullableId,
  method: z.enum(LINE_METHODS).catch('value'),
  quantity_to_date: decimal,
  percent_to_date: decimal,
  value_to_date: money,
  previous_value: money,
  this_valuation: money,
  included: z.boolean().catch(true).default(true),
  measured: bool,
  suggestion: z
    .object({ source: text, percent: decimal, value: money })
    .nullable()
    .catch(null)
    .default(null),
  note: text,
});
export type ValuationLine = z.infer<typeof valuationLineSchema>;

const totalsSchema = z
  .object({
    works: money,
    variations: money,
    claims: money,
    dayworks: money,
    materials: money,
    adjustments: money,
    gross: money,
    previous_gross: money,
    this_valuation: money,
  })
  .catch({
    works: null,
    variations: null,
    claims: null,
    dayworks: null,
    materials: null,
    adjustments: null,
    gross: null,
    previous_gross: null,
    this_valuation: null,
  });

export const measurementSchema = z.object({
  id: z.string(),
  project_id: z.string(),
  valuation_id: z.string(),
  line_id: z.string().default(''),
  boq_lineage: z.string().default(''),
  number: count,
  description: text,
  times: decimal,
  length: decimal,
  width: decimal,
  height: decimal,
  deduct: bool,
  quantity: decimal,
  drawing_revision_id: nullableId,
  zone_id: nullableId,
  location: text,
  measured_on: date,
  recorded_by_id: nullableId,
  brought_forward: bool,
  created_at: date,
  version,
  deleted: bool,
});
export type Measurement = z.infer<typeof measurementSchema>;

export const valuationSchema = z.object({
  id: z.string(),
  project_id: z.string(),
  contract_id: z.string().default(''),
  boq_version_id: nullableId,
  number: count,
  reference: text,
  valuation_date: z.string().default(''),
  status: z.string().default('draft'),
  prepared_by: personSchema,
  prepared_by_id: nullableId,
  prepared_by_party: commercialPartySchema,
  prepared_by_side: z.enum(['employer', 'contractor']).catch('employer'),
  totals: totalsSchema,
  notes: text,
  created_at: date,
  updated_at: date,
  version,
});
export type Valuation = z.infer<typeof valuationSchema>;

export const valuationDetailSchema = valuationSchema.extend({
  lines: z.array(valuationLineSchema).catch([]).default([]),
  measurements: z.array(measurementSchema).catch([]).default([]),
  certificate: z
    .object({ id: z.string(), reference: text, status: text })
    .nullable()
    .catch(null)
    .default(null),
  can: can('edit', 'measure'),
});
export type ValuationDetail = z.infer<typeof valuationDetailSchema>;

// ---------------------------------------------------------------------------
// Certificates and payments
// ---------------------------------------------------------------------------

export const paymentSchema = z.object({
  id: z.string(),
  amount: money,
  status: text,
  provider: text,
  method: z.string().nullable().default(null),
  reference: text,
  paid_on: date,
  note: text,
  recorded_by: personSchema,
  completed_at: date,
  created_at: date,
});
export type CertificatePayment = z.infer<typeof paymentSchema>;

const amountsSchema = z.object({
  gross_to_date: money,
  retention_to_date: money,
  advance_recovered_to_date: money,
  net_to_date: money,
  previous_net: money,
  retention_released: money,
  advance: money,
  adjustment: money,
  net_this: money,
  vat: money,
  wht: money,
  payable: money,
  paid: money,
  outstanding: money,
});
export type CertificateAmounts = z.infer<typeof amountsSchema>;

export const certificateSchema = z.object({
  id: z.string(),
  project_id: z.string().default(''),
  contract_id: z.string().default(''),
  number: count,
  reference: text,
  kind: z.enum(CERTIFICATE_KINDS).catch('interim'),
  valuation_id: nullableId,
  valuation_reference: z.string().nullable().default(null),
  valuation_date: date,
  release_stage: z.string().nullable().default(null),
  status: z.enum(CERTIFICATE_STATUSES).catch('draft'),
  amounts: amountsSchema,
  vat_percent: decimal,
  wht_percent: decimal,
  adjustment_note: text,
  payable_in_words: text,
  prepared_by: personSchema,
  prepared_by_id: nullableId,
  prepared_by_party: commercialPartySchema,
  prepared_by_side: z.enum(['employer', 'contractor']).catch('employer'),
  submitted_at: date,
  reviewed_by: personSchema,
  reviewed_at: date,
  review_note: text,
  contractor_agreed: z.boolean().nullable().catch(null).default(null),
  returned_note: text,
  certified_by: personSchema,
  certified_by_party: commercialPartySchema,
  certified_at: date,
  certifier_note: text,
  due_on: date,
  overdue_days: count,
  payment_approved_by: personSchema,
  payment_approved_at: date,
  obligation_id: nullableId,
  paid_at: date,
  created_at: date,
  version,
  can: can(
    'edit',
    'submit',
    'cancel',
    'review',
    'certify',
    'return',
    'approve_payment',
    'record_payment',
  ),
});
export type Certificate = z.infer<typeof certificateSchema>;

export const certificateDetailSchema = certificateSchema.extend({
  contract: contractSchema.nullable().catch(null).default(null),
  payments: z.array(paymentSchema).catch([]).default([]),
});
export type CertificateDetail = z.infer<typeof certificateDetailSchema>;

export const paymentsViewSchema = z.object({
  contract: contractSchema.nullable().catch(null).default(null),
  currency: z.string().default('KES'),
  totals: z
    .object({
      outstanding: money,
      overdue: money,
      overdue_count: count,
      paid_this_month: money,
      paid_to_date: money,
    })
    .catch({
      outstanding: null,
      overdue: null,
      overdue_count: 0,
      paid_this_month: null,
      paid_to_date: null,
    }),
  due: z.array(certificateSchema).catch([]).default([]),
  upcoming: z.array(certificateSchema).catch([]).default([]),
  paid: z.array(certificateSchema).catch([]).default([]),
  provider: z.string().default('sandbox'),
});
export type PaymentsView = z.infer<typeof paymentsViewSchema>;

// ---------------------------------------------------------------------------
// Overview, retention, reconciliation
// ---------------------------------------------------------------------------

const releaseDueSchema = z
  .object({
    stage: z.enum(['pc', 'dlp']).nullable().catch(null).default(null),
    held_minor: count,
    held: money,
    completion_date: date,
    dlp_end: date,
    released_stages: z.array(z.string()).catch([]).default([]),
  })
  .catch({
    stage: null,
    held_minor: 0,
    held: null,
    completion_date: null,
    dlp_end: null,
    released_stages: [],
  });

export const summarySchema = z.object({
  contract: contractSchema.nullable().catch(null).default(null),
  currency: z.string().default('KES'),
  figures: z
    .object({
      contract_sum: money,
      approved_variations: money,
      pending_variations: money,
      revised_contract_sum: money,
      gross_certified: money,
      certified_payable: money,
      paid: money,
      outstanding: money,
      overdue: money,
      retention_held: money,
      advance_outstanding: money,
      forecast_final_account: money,
      certified_percent: z.coerce.number().catch(0).default(0),
    })
    .nullable()
    .catch(null)
    .default(null),
  chain: z
    .array(z.object({ key: z.string(), amount: money }))
    .catch([])
    .default([]),
  counts: z
    .object({
      certificates: count,
      overdue: count,
      notices_open: count,
      notices_overdue: count,
      claims_open: count,
      disputes_open: count,
      variations_to_assess: count,
    })
    .nullable()
    .catch(null)
    .default(null),
  current_valuation: valuationSchema.nullable().catch(null).default(null),
  certificate_in_progress: certificateSchema.nullable().catch(null).default(null),
  release_due: releaseDueSchema.nullable().catch(null).default(null),
  recent_certificates: z.array(certificateSchema).catch([]).default([]),
});
export type CommercialSummary = z.infer<typeof summarySchema>;

export const retentionSchema = z.object({
  currency: z.string().default('KES'),
  retention_percent: z.string().default('0'),
  retention_limit: money,
  pc_release_percent: count,
  held: money,
  released: money,
  rows: z
    .array(
      z.object({
        certificate_id: z.string(),
        reference: text,
        kind: text,
        release_stage: z.string().nullable().default(null),
        certified_at: date,
        gross_to_date: money,
        change: money,
        balance: money,
        status: text,
      }),
    )
    .catch([])
    .default([]),
  release_due: releaseDueSchema,
  ledger_balance: money,
});
export type Retention = z.infer<typeof retentionSchema>;

export const reconciliationSchema = z.object({
  currency: z.string().default('KES'),
  rows: z
    .array(
      z.object({
        certificate_id: z.string(),
        reference: text,
        kind: text,
        certified_at: date,
        due_on: date,
        payable: money,
        paid: money,
        outstanding: money,
        overdue_days: count,
        status: text,
        payments: z.array(paymentSchema).catch([]).default([]),
        matches: z.boolean().catch(true).default(true),
      }),
    )
    .catch([])
    .default([]),
  totals: z.object({ payable: money, paid: money, outstanding: money }),
  ledger: z.object({
    certificates_payable: money,
    retention_held: money,
    contract_works: money,
    balanced: bool,
    reconciled: bool,
  }),
});
export type Reconciliation = z.infer<typeof reconciliationSchema>;

// ---------------------------------------------------------------------------
// Sums, adjustments, variations
// ---------------------------------------------------------------------------

export const sumExpenditureSchema = z.object({
  id: z.string(),
  boq_lineage: text,
  description: text,
  amount: money,
  instructed_on: date,
  reference: text,
  nominee: text,
  instruction_id: nullableId,
  variation_id: nullableId,
  recorded_by: personSchema,
  created_at: date,
});
export type SumExpenditure = z.infer<typeof sumExpenditureSchema>;

export const sumsSchema = z.object({
  currency: z.string().default('KES'),
  rows: z
    .array(
      z.object({
        boq_lineage: z.string(),
        item_code: text,
        description: text,
        kind: text,
        section: text,
        allowance: money,
        expended: money,
        balance: money,
        expenditures: z.array(sumExpenditureSchema).catch([]).default([]),
      }),
    )
    .catch([])
    .default([]),
  totals: z.object({ allowance: money, expended: money, balance: money }),
  can_record: bool,
});
export type Sums = z.infer<typeof sumsSchema>;

export const adjustmentSchema = z.object({
  id: z.string(),
  number: count,
  reference: text,
  category: z.string().default('other'),
  description: text,
  amount: money,
  status: z.string().default('proposed'),
  raised_by: personSchema,
  raised_by_party: commercialPartySchema,
  decided_by: personSchema,
  decided_at: date,
  note: text,
  created_at: date,
  can: can('respond', 'withdraw'),
});
export type Adjustment = z.infer<typeof adjustmentSchema>;

export const adjustmentsSchema = z.object({
  rows: z.array(adjustmentSchema).catch([]).default([]),
  can_propose: bool,
});

export const variationRowSchema = z.object({
  id: z.string(),
  number: count,
  reference: text,
  title: text,
  category: text,
  description: text,
  status: text,
  cost_impact: money,
  time_impact_days: count,
  requested_by_name: z.string().nullable().default(null),
  requested_by_party: text,
  submitted_at: date,
  decided_at: date,
  assessment: z
    .object({
      status: z.string().nullable().default(null),
      basis: z.string().nullable().default(null),
      rates_note: text,
      assessed: money,
      assessed_days: z.number().nullable().catch(null).default(null),
      note: text,
      assessed_by_name: z.string().nullable().default(null),
      assessed_at: date,
      agreed: money,
      agreed_days: z.number().nullable().catch(null).default(null),
      agreed_by_name: z.string().nullable().default(null),
      agreed_at: date,
      value: money,
    })
    .catch({
      status: null,
      basis: null,
      rates_note: '',
      assessed: null,
      assessed_days: null,
      note: '',
      assessed_by_name: null,
      assessed_at: null,
      agreed: null,
      agreed_days: null,
      agreed_by_name: null,
      agreed_at: null,
      value: null,
    }),
  certified_to_date: money,
  can: can('assess', 'respond'),
});
export type VariationRow = z.infer<typeof variationRowSchema>;

// ---------------------------------------------------------------------------
// Notices, claims, disputes
// ---------------------------------------------------------------------------

export const noticeSchema = z.object({
  id: z.string(),
  number: count,
  reference: text,
  kind: z.string().default('other'),
  subject: text,
  body: text,
  clause: text,
  from_party: commercialPartySchema,
  to_party: commercialPartySchema,
  issued_by: personSchema,
  issued_on: date,
  event_date: date,
  time_bar_days: z.number().nullable().catch(null).default(null),
  notice_deadline: date,
  late: bool,
  response_due_on: date,
  response_overdue: bool,
  response_due_soon: bool,
  status: z.string().default('open'),
  response: text,
  responded_by: personSchema,
  responded_at: date,
  created_at: date,
  can: can('respond', 'close'),
});
export type Notice = z.infer<typeof noticeSchema>;

export const noticesSchema = z.object({
  rows: z.array(noticeSchema).catch([]).default([]),
  can_issue: bool,
  to_parties: z.array(commercialPartySchema).catch([]).default([]),
});

export const claimSchema = z.object({
  id: z.string(),
  number: count,
  reference: text,
  kind: z.string().default('both'),
  title: text,
  description: text,
  clause: text,
  notice_ids: z.array(z.string()).catch([]).default([]),
  days_claimed: count,
  amount_claimed: money,
  days_assessed: z.number().nullable().catch(null).default(null),
  amount_assessed: money,
  days_agreed: z.number().nullable().catch(null).default(null),
  amount_agreed: money,
  status: z.string().default('submitted'),
  submitted_by: personSchema,
  submitted_by_party: commercialPartySchema,
  assessed_by: personSchema,
  assessed_at: date,
  assessment_note: text,
  decided_by: personSchema,
  decided_at: date,
  decision_note: text,
  created_at: date,
  can: can('assess', 'reject', 'respond', 'withdraw'),
});
export type Claim = z.infer<typeof claimSchema>;

export const claimsSchema = z.object({
  rows: z.array(claimSchema).catch([]).default([]),
  can_submit: bool,
});

export const disputeSchema = z.object({
  id: z.string(),
  number: count,
  reference: text,
  title: text,
  description: text,
  source_type: text,
  source_id: nullableId,
  source_reference: text,
  amount_in_dispute: money,
  status: z.string().default('open'),
  raised_by: personSchema,
  raised_by_party: commercialPartySchema,
  resolution: text,
  resolved_amount: money,
  resolved_by: personSchema,
  resolved_at: date,
  created_at: date,
  updated_at: date,
});
export type Dispute = z.infer<typeof disputeSchema>;

export const disputesSchema = z.object({
  rows: z.array(disputeSchema).catch([]).default([]),
  can_manage: bool,
});

// ---------------------------------------------------------------------------
// The final account
// ---------------------------------------------------------------------------

export const finalAccountSchema = z.object({
  id: nullableId,
  status: z.string().nullable().default(null),
  live: bool,
  figures: z.object({
    contract_sum: money,
    sums_omitted: money,
    sums_expended: money,
    variations: money,
    claims: money,
    adjustments: money,
    final_value: money,
    certified_to_date: money,
    balance: money,
  }),
  prepared_by: personSchema,
  submitted_at: date,
  contractor_agreed_by: personSchema,
  contractor_agreed_at: date,
  employer_agreed_by: personSchema,
  employer_agreed_at: date,
  note: text,
  can: can('prepare', 'submit', 'reopen', 'contractor_agree', 'employer_agree'),
  sums: z
    .object({ allowance: money, expended: money, balance: money })
    .nullable()
    .catch(null)
    .default(null),
});
export type FinalAccount = z.infer<typeof finalAccountSchema>;

// ---------------------------------------------------------------------------
// Cost control, cash flow, cost plans
// ---------------------------------------------------------------------------

const controlRowSchema = z.object({
  section: z.string().nullable().default(null),
  budget: money,
  variations: money,
  revised: money,
  certified: money,
  forecast: money,
  variance: money,
  committed: money.optional(),
  actual: money.optional(),
});
export type ControlRow = z.infer<typeof controlRowSchema>;

export const costControlSchema = z.object({
  available: bool,
  currency: z.string().default('KES'),
  internal: bool,
  rows: z.array(controlRowSchema).catch([]).default([]),
  totals: controlRowSchema
    .omit({ section: true })
    .extend({ paid: money })
    .nullable()
    .catch(null)
    .default(null),
  methods: z.record(z.string(), z.string()).catch({}).default({}),
  earned_value: z
    .object({
      budget_at_completion: money,
      earned_value: money,
      actual_cost: money,
      cpi: z.number().nullable().catch(null).default(null),
      spi: z.number().nullable().catch(null).default(null),
      eac: money,
    })
    .nullable()
    .catch(null)
    .default(null),
});
export type CostControl = z.infer<typeof costControlSchema>;

export const cashFlowRowSchema = z.object({
  month: z.string(),
  month_end: z.string().default(''),
  planned_cumulative: money,
  certified_cumulative: money,
  paid_cumulative: money,
  planned_month: money,
  certified_month: money,
  paid_month: money,
  future: bool,
});
export type CashFlowRow = z.infer<typeof cashFlowRowSchema>;

export const cashFlowSchema = z.object({
  available: bool,
  currency: z.string().default('KES'),
  budget: money,
  start: date,
  end: date,
  rows: z.array(cashFlowRowSchema).catch([]).default([]),
  methods: z.record(z.string(), z.string()).catch({}).default({}),
});
export type CashFlow = z.infer<typeof cashFlowSchema>;

export const costPlanElementSchema = z.object({
  id: z.string(),
  code: text,
  name: text,
  quantity: decimal,
  unit: text,
  rate: money,
  amount: money,
  notes: text,
  sequence: count,
});
export type CostPlanElement = z.infer<typeof costPlanElementSchema>;

export const costPlanVersionSchema = z.object({
  id: z.string(),
  number: count,
  label: text,
  status: z.string().default('draft'),
  total: money,
  per_sqm: money,
  notes: text,
  issued_by: personSchema,
  issued_at: date,
  elements: z.array(costPlanElementSchema).catch([]).default([]),
});
export type CostPlanVersion = z.infer<typeof costPlanVersionSchema>;

export const costPlanSchema = z.object({
  id: z.string(),
  name: text,
  currency: z.string().default('KES'),
  party: commercialPartySchema,
  gross_floor_area_sqm: decimal,
  created_by: personSchema,
  created_at: date,
  versions: z.array(costPlanVersionSchema).catch([]).default([]),
  latest_total: money,
  can_manage: bool,
});
export type CostPlan = z.infer<typeof costPlanSchema>;

export const costPlansSchema = z.object({
  rows: z.array(costPlanSchema).catch([]).default([]),
  can_manage: bool,
});
